import { Injectable } from '@angular/core';
import { EMPTY, Observable, exhaustMap, filter, first, from, fromEvent, map, merge, timer } from 'rxjs';
import { APP_VERSION } from 'src/app/version';

// TEMP: dummy change to trigger a new deployment for testing the update notification. Remove afterwards.

/** How often to check whether a newer version has been deployed. */
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

/** Contents of version.json, written next to the app by scripts/set-version.js. */
interface DeployedVersion {
  version?: string;
  commit?: string;
}

/**
 * Detects that a newer version of the application has been deployed, by comparing version.json
 * on the server with the version compiled into the running application.
 * Polls instead of using a service worker, the same way as executor-client.
 */
@Injectable({
  providedIn: 'root',
})
export class UpdateService {
  /**
   * Emits once when the deployed version differs from the running version.
   * Checks periodically and whenever the tab becomes visible again.
   * Never emits for local builds, which have no pipeline version.
   */
  public readonly updateAvailable$: Observable<void> = this.isLocalBuild()
    ? EMPTY
    : merge(
        timer(CHECK_INTERVAL_MS, CHECK_INTERVAL_MS),
        fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')),
      ).pipe(
        exhaustMap(() => from(this.fetchDeployedVersion())),
        filter(deployedVersion => this.isDifferentVersion(deployedVersion)),
        first(),
        map(() => undefined),
      );

  /**
   * Reloads the application to get the new version.
   */
  public reloadApplication(): void {
    document.location.reload();
  }

  /**
   * Gets version.json from the server, bypassing all caches.
   * @returns Deployed version, or null if it could not be read.
   */
  private async fetchDeployedVersion(): Promise<DeployedVersion | null> {
    try {
      const url = new URL(`version.json?t=${Date.now()}`, document.baseURI);
      const response = await fetch(url, { cache: 'no-store' });
      return response.ok ? ((await response.json()) as DeployedVersion) : null;
    } catch {
      // Network errors, or a non-JSON response such as index.html served for a missing file.
      return null;
    }
  }

  /**
   * Checks whether the deployed version differs from the running version.
   * @param deployedVersion Deployed version.
   * @returns True if a different version is deployed.
   */
  private isDifferentVersion(deployedVersion: DeployedVersion | null): boolean {
    return !!deployedVersion?.version && (deployedVersion.version !== APP_VERSION.version || deployedVersion.commit !== APP_VERSION.commit);
  }

  /**
   * Checks whether this is a local build, where version checks make no sense.
   * @returns True for local builds.
   */
  private isLocalBuild(): boolean {
    return APP_VERSION.version.endsWith('.local');
  }
}
